import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { orgRoute } from '@/app/org-route';

// SSensorNew — pair a sensor outside the commissioning wizard. The
// wizard's "pair" step is a multi-step flow; here we expose only the
// first step (mint a pairing token + sensor row) and link to the
// existing commissioning screen for the rest.
export function SSensorNew() {
  const apiConfig = useApiConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serial, setSerial] = useState('');
  const [model, setModel] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [createdSensorId, setCreatedSensorId] = useState<string | null>(null);

  const pair = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(apiConfig, '/v1/sensors/pair', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serial_number: serial.trim(), model: model.trim() }),
      });
      if (!res.ok) throw new Error('failed to pair sensor');
      return (await res.json()) as { sensorId?: string; pairing_token?: string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['sensors'] });
      if (data.sensorId) setCreatedSensorId(data.sensorId);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (!serial.trim()) {
      setFormError('Serial number is required.');
      return;
    }
    pair.mutate();
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-sp-6 px-sp-6 py-sp-9">
      <header className="flex flex-col gap-sp-2">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Analyze
        </p>
        <h1 className="text-fs-h1 font-medium text-gray-900">Pair sensor</h1>
        <p className="text-fs-body text-gray-700">
          Enter the sensor's serial number to register it with your tenant.
          You can assign it to an asset after pairing.
        </p>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-sp-5 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Serial number
          <input
            required
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="SN-2026-0042"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 font-mono"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Model (optional)
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="VibePro-3"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
          />
        </label>
        {formError ? (
          <p role="alert" className="text-fs-meta text-critical">{formError}</p>
        ) : null}
        {createdSensorId ? (
          <div className="rounded-r-default border border-success bg-success-bg px-sp-3 py-sp-2 text-fs-body text-success">
            Sensor paired as <code className="font-mono">{createdSensorId}</code>.
            Continue commissioning to assign it to an asset.
          </div>
        ) : null}
        <div className="flex items-center gap-sp-3">
          <button
            type="submit"
            disabled={pair.isPending}
            className="rounded-r-default bg-info px-sp-4 py-sp-2 text-fs-body font-medium text-gray-50 disabled:opacity-60"
          >
            {pair.isPending ? 'Pairing…' : 'Pair sensor'}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: orgRoute('/commissioning') })}
            className="rounded-r-default border border-gray-300 px-sp-4 py-sp-2 text-fs-body text-gray-900"
          >
            Open commissioning wizard
          </button>
        </div>
      </form>
    </div>
  );
}
